import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, Image, Linking } from "react-native";
import { useRouter } from "expo-router";
import Head from "expo-router/head";
import { PublicNav } from "../../../components/PublicNav";

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const TEXT = "#1F2937";

// Article sections — written for someone whose dog has never run Fast CAT.
// Tries to be genuinely useful: prerequisites, real drills, what to skip.
export default function PostTrainForFastCat() {
  const router = useRouter();

  return (
    <View style={{ flex: 1, backgroundColor: WHITE }}>
      {Platform.OS === "web" && (
        <Head>
          <title>How to Train Your Dog for Fast CAT — Dash-Hound</title>
          <meta name="description" content="Step-by-step guide to training your dog for AKC Fast CAT: prerequisites, building prey drive, introducing the lure, practice drills, conditioning, and what to do on run day." />
          <meta property="og:title" content="How to Train Your Dog for Fast CAT — Dash-Hound" />
          <meta property="og:description" content="Step-by-step guide to training your dog for AKC Fast CAT, from prerequisites to run-day prep." />
          <meta property="og:url" content="https://dashhound.app/blog/how-to-train-your-dog-for-fast-cat" />
          <meta property="og:type" content="article" />
          <meta name="twitter:title" content="How to Train Your Dog for Fast CAT — Dash-Hound" />
          <meta name="twitter:description" content="Step-by-step guide to training your dog for AKC Fast CAT, from prerequisites to run-day prep." />
        </Head>
      )}

      <PublicNav />

      <View style={styles.subBar}>
        <TouchableOpacity onPress={() => router.push("/(auth)/blog" as any)}>
          <Text style={styles.subBarBack}>← Back to Blog</Text>
        </TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.hero}>
          <Text style={styles.heroCategory}>TRAINING</Text>
          <Text style={styles.heroTitle}>How to Train Your Dog for Fast CAT</Text>
          <Text style={styles.heroMeta}>June 14, 2026 · 8 min read</Text>
        </View>

        <View style={styles.body}>
          <Text style={styles.lead}>
            Fast CAT looks simple — your dog runs 100 yards chasing a plastic bag — but a lot of first-timers show up, get released at the start line, and watch their dog jog 30 feet, sniff something, and trot back. The dogs that rip through to the finish are the ones whose owners spent a few weeks building drive and conditioning at home. Here's how to do it.
          </Text>

          <Figure
            src="/blog/mogwai-chasing-lure.jpg"
            alt="Dash-Squad member Mogwai mid-air chasing the lure at a Fast CAT event"
            caption="This is the goal — full chase, full focus, all four feet off the ground. Dash-Squad member Mogwai mid-stride."
          />

          <H2>1. Make Sure Your Dog Is Actually Ready</H2>
          <Text style={styles.p}>
            Before you sign up for anything, do an honest assessment:
          </Text>
          <Bullet>
            <Strong>Age:</Strong> AKC's minimum is 12 months. Don't push under-developed joints on a young dog who'll be at full sprint for the first time.
          </Bullet>
          <Bullet>
            <Strong>Health:</Strong> No recent injuries, no significant arthritis, healthy weight. If your dog hasn't been to the vet in over a year and you're going to ask them to sprint flat-out, get a wellness check first.
          </Bullet>
          <Bullet>
            <Strong>Prey drive:</Strong> Does your dog chase things — squirrels, toys, anything that moves? Some dogs (looking at you, Frenchies and basset hounds) are physically capable but mentally uninterested. That's fine. Don't force it.
          </Bullet>
          <Bullet>
            <Strong>Conditioning:</Strong> A pet dog who walks twice a day isn't ready to sprint 100 yards. If your dog gets winded chasing a ball across the yard three times, you have a few weeks of base building ahead of you.
          </Bullet>

          <H2>2. Build Drive at Home With a Flirt Pole</H2>
          <Text style={styles.p}>
            A flirt pole is the single best at-home training tool for Fast CAT. It's basically a fishing pole with a fuzzy lure on the end of a long string. You drag the lure across the ground in unpredictable patterns and your dog chases. Twenty bucks on Amazon.
          </Text>
          <Text style={styles.p}>
            Two rules: <Strong>never let them catch it on purpose</Strong> (you're building chase drive, not capture satisfaction), and <Strong>stop while they still want more</Strong>. Three to five minute sessions, two or three times a week. If your dog disengages, you're going too long.
          </Text>
          <Text style={styles.p}>
            What you're training: the dog learns that fuzzy thing moving fast across the ground = the most exciting thing in the world. That's the exact mental state you want at the start line.
          </Text>

          <H2>3. Build Recall to a Person at the Finish</H2>
          <Text style={styles.p}>
            On event day there will be a person at the finish line ("the catcher") holding the dog's favorite toy or treat. Your dog needs to know to run TO that person, hard, without veering. Practice this:
          </Text>
          <Bullet>
            Two people, big open space. One holds the dog, the other walks 30 yards away with a high-value toy or treats. On a "ready, ready, ready... GO!", the holder releases and the runner shouts the dog's name with full energy.
          </Bullet>
          <Bullet>
            Reward big when the dog gets there. Then walk back and do it again — but only 3–5 reps per session. End on a success.
          </Bullet>
          <Bullet>
            Gradually stretch the distance to 100 yards over a couple of weeks.
          </Bullet>
          <Text style={styles.p}>
            If your dog blows past the catcher and keeps going — great problem to have, easy to fix at the actual event with a back-up catcher and a fence.
          </Text>

          <H2>4. Introduce the Lure</H2>
          <Text style={styles.p}>
            If you can find a local lure-coursing or Fast CAT practice (search Facebook for "Fast CAT practice" + your state), go to at least one before paying for a real event. You'll learn whether your dog is into the white-bag lure or not. Some dogs go nuts. Some look at it like "that's a plastic bag, why."
          </Text>
          <Text style={styles.p}>
            If you can't get to a practice, you can simulate at home with a long string, a fishing reel, and a white plastic bag. Drag the bag across grass in a straight line away from your dog and let them chase it. Same flirt-pole rules: don't let them catch it.
          </Text>

          <H2>5. Condition the Sprint</H2>
          <Text style={styles.p}>
            Most pet dogs have endurance but not sprint power. Build that with short bursts: 50–100 yards of all-out fetch on grass, 3–5 reps, every other day, for 2–3 weeks before your first event. <Strong>Skip pavement</Strong> — high-speed turns and stops on hard surfaces tear up paw pads and ligaments.
          </Text>
          <Text style={styles.p}>
            If your dog is already a fetch fanatic, you're probably fine. If your dog is more "I'll bring the ball back if I feel like it," use the flirt pole or a chuck-it instead — the goal is high-effort, short-duration sprints.
          </Text>

          <H2>6. The Week Before Your First Event</H2>
          <Bullet>
            <Strong>Drop intensity 2–3 days out.</Strong> Easy walks only. Don't show up with sore muscles.
          </Bullet>
          <Bullet>
            <Strong>Confirm what to bring:</Strong> rabies certificate (paper copy, not just a tag), water, a crate or shade, treats, your dog's collar (no harness — many clubs allow martingales or quick-release only).
          </Bullet>
          <Bullet>
            <Strong>Walk the venue if you can.</Strong> Some clubs let you walk the course the morning of. Take your dog for a sniff so the environment isn't brand new at run time.
          </Bullet>
          <Bullet>
            <Strong>Don't feed a big meal within 2–3 hours of run time.</Strong> Sprint + full stomach is a recipe for a vomiting dog or — much worse — bloat in deep-chested breeds.
          </Bullet>

          <H2>7. Run Day</H2>
          <Text style={styles.p}>
            Show up 30–45 minutes before your run time. Check in, get your bib number, find your spot. Most events have a "tally ho" call right before release — the lure operator starts moving the bag and someone yells "TALLY HO!" That's your signal.
          </Text>
          <Text style={styles.p}>
            At the start line: <Strong>face the dog away from the lure</Strong> until the operator starts. You want the dog to see the moving lure for the first time as a SURPRISE, not as something they've been staring at for two minutes (which leads to over-arousal or confusion).
          </Text>
          <Text style={styles.p}>
            Release on the "tally ho." Don't throw the dog forward — just let go cleanly. Then get out of the way and watch them rip. The catcher at the other end will grab them; you walk down to meet them with treats.
          </Text>

          <H2>8. The Most Common First-Time Mistakes</H2>
          <Bullet>
            <Strong>Letting the dog see the lure too long before release.</Strong> Dogs lose their minds, then lose interest. Keep them facing away or distracted until "go."
          </Bullet>
          <Bullet>
            <Strong>Throwing treats AT the finish.</Strong> The catcher's job is to redirect the dog after the line. Treats happen calmly, not in a frenzy that teaches the dog the finish line means a free-for-all.
          </Bullet>
          <Bullet>
            <Strong>Running a dog who's pulling up lame.</Strong> If your dog limps even slightly after a practice sprint, scratch the entry. There's another event next month.
          </Bullet>
          <Bullet>
            <Strong>Comparing your time to the breed leader.</Strong> Your first run is a baseline. The dogs at the top of the rankings have been doing this for years. Race your own dog, not the rankings.
          </Bullet>

          <H2>9. After the Run</H2>
          <Text style={styles.p}>
            Walk your dog around for 5–10 minutes to cool down before crating. Offer water in small amounts. If you're staying for a second run (most events offer 2 per day), watch for signs of overheating — heavy panting that doesn't slow down, glassy eyes, weakness. Skip the second run if anything feels off.
          </Text>
          <Text style={styles.p}>
            Log your time in Dash-Hound when you get home so you can track progress event to event. Most dogs improve their MPH by 1–3 over their first 5 events as they figure out the game.
          </Text>

          <View style={styles.cta}>
            <Text style={styles.ctaTitle}>Ready to find your first event?</Text>
            <Text style={styles.ctaText}>
              Browse upcoming Fast CAT events near you in the Dash-Hound app — or read the full sign-up guide if you haven't registered your dog with AKC yet.
            </Text>
            <View style={{ flexDirection: "row", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
              <TouchableOpacity style={styles.ctaPrimary} onPress={() => router.push("/(auth)/how-to-signup" as any)}>
                <Text style={styles.ctaPrimaryText}>Sign-up Guide →</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.ctaSecondary} onPress={() => router.push("/(auth)/register" as any)}>
                <Text style={styles.ctaSecondaryText}>Get Dash-Hound</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>© 2026 Dash-Hound LLC. All rights reserved.</Text>
        </View>
      </ScrollView>
    </View>
  );
}

function H2({ children }: { children: string }) {
  return <Text style={styles.h2}>{children}</Text>;
}
function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <View style={styles.bulletRow}>
      <Text style={styles.bulletDot}>•</Text>
      <Text style={styles.bulletText}>{children}</Text>
    </View>
  );
}
function Strong({ children }: { children: string }) {
  return <Text style={{ fontWeight: "700", color: NAVY }}>{children}</Text>;
}
function Figure({ src, alt, caption }: { src: string; alt: string; caption?: string }) {
  return (
    <View style={styles.figure}>
      <View style={styles.figureFrame}>
        {Platform.OS === "web" ? (
          // @ts-ignore — raw img on web for proper layout + sharp render
          <img src={src} alt={alt} style={{ width: "100%", height: "auto", display: "block" } as any} />
        ) : (
          <Image source={{ uri: `https://dashhound.app${src}` }} style={{ width: "100%", aspectRatio: 16 / 10 }} resizeMode="cover" />
        )}
      </View>
      {caption ? <Text style={styles.figureCaption}>{caption}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  subBar: {
    backgroundColor: "#FFF7E6",
    borderBottomWidth: 1,
    borderBottomColor: "#F5DEB3",
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  subBarBack: { color: "#92400E", fontSize: 14, fontWeight: "600" },
  content: { flexGrow: 1 },
  hero: {
    backgroundColor: "#FFF7E6",
    paddingHorizontal: 24,
    paddingTop: 32,
    paddingBottom: 40,
    alignItems: "center",
  },
  heroCategory: { color: "#92400E", fontSize: 12, fontWeight: "800", letterSpacing: 1, marginBottom: 8 },
  heroTitle: { color: NAVY, fontSize: 30, fontWeight: "900", textAlign: "center", maxWidth: 720, lineHeight: 38 },
  heroMeta: { color: "#92400E", fontSize: 13, marginTop: 12 },
  body: {
    maxWidth: 720,
    alignSelf: "center",
    width: "100%",
    paddingHorizontal: 20,
    paddingVertical: 32,
  },
  lead: { fontSize: 17, color: TEXT, lineHeight: 28, marginBottom: 8, fontWeight: "500" },
  h2: { fontSize: 22, fontWeight: "800", color: NAVY, marginTop: 32, marginBottom: 12 },
  p: { fontSize: 16, color: TEXT, lineHeight: 26, marginBottom: 12 },
  bulletRow: { flexDirection: "row", marginBottom: 8, paddingLeft: 4 },
  bulletDot: { fontSize: 16, color: ORANGE, marginRight: 10, lineHeight: 26, fontWeight: "700" },
  bulletText: { flex: 1, fontSize: 16, color: TEXT, lineHeight: 26 },
  figure: { marginVertical: 20 },
  figureFrame: { borderRadius: 14, overflow: "hidden", backgroundColor: "#F3F4F6", borderWidth: 1, borderColor: "#E5E7EB" },
  figureCaption: { fontSize: 13, color: "#6B7280", marginTop: 8, fontStyle: "italic", textAlign: "center", lineHeight: 20 },
  cta: {
    marginTop: 40,
    padding: 22,
    backgroundColor: "#F9FAFB",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  ctaTitle: { fontSize: 20, fontWeight: "800", color: NAVY, marginBottom: 8 },
  ctaText: { fontSize: 15, color: TEXT, lineHeight: 22 },
  ctaPrimary: { backgroundColor: NAVY, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 8 },
  ctaPrimaryText: { color: WHITE, fontWeight: "700", fontSize: 14 },
  ctaSecondary: { backgroundColor: ORANGE, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 8 },
  ctaSecondaryText: { color: NAVY, fontWeight: "800", fontSize: 14 },
  footer: { backgroundColor: NAVY_DARK, paddingVertical: 32, alignItems: "center" },
  footerText: { color: "rgba(255,255,255,0.35)", fontSize: 13 },
});
